import { capitalizeString } from '@order-management/format';
import {
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  WorkOrderStatus,
  WorkOrderSummaryData,
  WorkOrderType,
  WorkOrdersCriteria,
} from '@order-management/types';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import { Element } from 'react-bulma-components';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useUpdateUrl from '../../shared/hooks/url';
import { OnTableChangeData } from '../../ui/table/types';
import WorkOrdersTable from '../work-orders-table/work-orders-table';

export interface WorkOrdersTableCardProps {
  initialCriteria: WorkOrdersCriteria;
  criteria: WorkOrdersCriteria;
  setCriteria: Dispatch<SetStateAction<WorkOrdersCriteria>>;
  tableData: PaginatedAPIResponse<WorkOrderSummaryData>;
  workOrdersStatus: WorkOrderStatus;
  emptyTableContent: ReactNode;
  emptyTableContentWithFilters: ReactNode;
}

export function WorkOrdersTableCard({
  initialCriteria,
  criteria,
  setCriteria,
  tableData,
  workOrdersStatus,
  emptyTableContent,
  emptyTableContentWithFilters,
}: WorkOrdersTableCardProps) {
  const updateUrl = useUpdateUrl();

  const filtersData = useMemo(() => {
    return {
      workOrderName: {
        title: 'Work order name',
        appliedCount: criteria.workOrderName?.length,
        value: criteria.workOrderName,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'workOrderName',
          valueKey: 'workOrderName',
          placeholder: 'Search by Work order name',
        },
      },
      accounts: {
        title: 'Account',
        appliedCount: criteria.accounts?.length,
        value: criteria.accounts,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'accounts',
          valueKey: 'accounts',
          placeholder: 'Search by Account',
        },
      },
      salesOrders: {
        title: 'Sales order',
        appliedCount: criteria.salesOrders?.length,
        value: criteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      type: {
        title: 'Type',
        appliedCount: criteria.type?.length,
        value: criteria.type,
        input: {
          type: InputType.SELECT,
          valueKey: 'type',
          options: Object.values(WorkOrderType).map((value) => ({
            label: capitalizeString(value.replace('_', ' ')),
            value,
          })),
        },
      },
      createdBy: {
        title: 'Created by',
        appliedCount: criteria.createdBy?.length,
        value: criteria.createdBy,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'createdBy',
          valueKey: 'createdBy',
          placeholder: 'Search by Creator',
        },
      },
    };
  }, [
    criteria.accounts,
    criteria.createdBy,
    criteria.salesOrders,
    criteria.type,
    criteria.workOrderName,
  ]) as FiltersData<WorkOrdersCriteria>;

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<WorkOrderSummaryData>): void => {
      const newData = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newData);
      updateUrl(newData);
    },
    [criteria, setCriteria, updateUrl]
  );

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({ ...criteria, pages: tableData.pages }),
    [criteria, tableData.pages]
  );

  const hasAppliedFilters = useMemo(
    () =>
      Object.entries(filtersData).some(([_, filter]) => filter.appliedCount),
    [filtersData]
  );

  const emptyContent = useMemo(() => {
    const emptyContentText = hasAppliedFilters
      ? emptyTableContentWithFilters
      : emptyTableContent;
    return <span className="has-text-weight-light">{emptyContentText}</span>;
  }, [emptyTableContent, emptyTableContentWithFilters, hasAppliedFilters]);

  const handleTableFilterSubmit = useCallback(
    (value: WorkOrdersCriteria): void => {
      const newCriteria: WorkOrdersCriteria = {
        ...value,
        itemsPerPage: criteria.itemsPerPage,
        pageNumber: 1,
        search: criteria.search,
        sortBy: criteria.sortBy,
        status: workOrdersStatus,
      };
      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, setCriteria, updateUrl, workOrdersStatus]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof WorkOrdersCriteria)[]) => {
      const newData = { ...criteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [criteria, handleTableFilterSubmit, initialCriteria]
  );

  return (
    <>
      <Element display="flex" className="w-full mb-2">
        <FilterBar
          initialData={initialCriteria}
          data={criteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
      <WorkOrdersTable
        dataSource={tableData.data}
        onChange={handleOnTableChange}
        config={tableConfig}
        form={{
          enableSearch: false,
        }}
        empty={emptyContent}
      />
    </>
  );
}

export default WorkOrdersTableCard;
